/**
 * Supabase Authentication
 *
 * The app used to authenticate against a REACT_APP_USERS env var and store the
 * session in sessionStorage. That never established a Supabase session, so every
 * PostgREST request ran as the `anon` role with auth.uid() = NULL — once RLS was
 * tightened on the shared database, the app could no longer read its own rows.
 *
 * Now backed by supabase.auth so requests run as `authenticated`.
 *
 * getCurrentUser() stays synchronous (six call sites depend on it) by reading a
 * module-level cache kept in sync by onAuthStateChange. Call initAuth() once on
 * startup and await it before reading the cache.
 */

import { supabase, Tables } from '../lib/supabase';

// Synchronous view of the current user, refreshed by onAuthStateChange.
let cachedUser = null;
let initPromise = null;

/**
 * Build the { email, username, role } shape the rest of the app expects.
 * Role comes from public.users; falls back to 'user' if the row is unreadable.
 */
async function toAppUser(authUser) {
  if (!authUser) return null;

  const emailLocalPart = (authUser.email || '').split('@')[0];
  const fallbackUsername = emailLocalPart
    ? emailLocalPart.charAt(0).toUpperCase() + emailLocalPart.slice(1)
    : 'User';

  const { data: profile, error } = await supabase
    .from(Tables.USERS)
    .select('username, role, full_name')
    .eq('id', authUser.id)
    .maybeSingle();

  if (error) {
    console.warn('Could not read public.users profile, using defaults:', error.message);
  }

  return {
    id: authUser.id,
    email: authUser.email,
    username: profile?.username || authUser.user_metadata?.full_name || fallbackUsername,
    role: profile?.role || 'user'
  };
}

/**
 * Restore any persisted session and subscribe to auth changes.
 * Idempotent — safe to call from React StrictMode's double mount.
 */
export function initAuth() {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const { data: { session } } = await supabase.auth.getSession();
    cachedUser = await toAppUser(session?.user);

    // The callback must stay synchronous: supabase-js holds an internal lock while
    // dispatching it, and toAppUser() issues a supabase.from() query that re-enters
    // getSession(). Awaiting inside the callback can deadlock on SIGNED_IN and on
    // every hourly TOKEN_REFRESHED. Defer the profile read out of the lock instead.
    supabase.auth.onAuthStateChange((_event, nextSession) => {
      setTimeout(async () => {
        cachedUser = await toAppUser(nextSession?.user);
      }, 0);
    });

    return cachedUser;
  })();

  return initPromise;
}

/**
 * Authenticate user with email and password
 */
export async function login(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: email.trim(),
    password
  });

  if (error) {
    return { success: false, error: error.message || 'Invalid email or password' };
  }

  cachedUser = await toAppUser(data.user);
  return { success: true, user: cachedUser };
}

/**
 * Start the Google OAuth redirect. Resolves before the redirect completes;
 * the session lands on the return trip and onAuthStateChange picks it up.
 */
export async function loginWithGoogle() {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.origin + window.location.pathname }
  });

  if (error) {
    return { success: false, error: error.message || 'Google sign-in failed' };
  }

  return { success: true };
}

/**
 * Logout current user
 */
export async function logout() {
  const { error } = await supabase.auth.signOut();
  cachedUser = null;

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Get current authenticated user (synchronous — reads the cache).
 */
export function getCurrentUser() {
  return cachedUser;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return cachedUser !== null;
}
